import React, { useState, useEffect, useMemo, useRef } from 'react';
import Button from '../../lib/button/Button';
import {
	EntityWithValueState,
	LiteralValueState,
	ChangeSet,
	ProjectState,
	getParentCanvasEntity,
	StateMutationAction
} from '@elyx-code/project-logic-tree';
import { DraggableObject } from '../../lib/canvas';
import {
	DynamicValueTypes,
	IValueStoreClient
} from '../../services/editor/value-store';
import { EditorService } from '../../services/editor';
import { handleLiteralValueChange } from '../../services/execution/value-resolution';
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Chip,
	Stack,
	Box,
	Tooltip,
	Alert
} from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DataObjectIcon from '@mui/icons-material/DataObject';

export interface IBytesEditorDialogProps {
	onClose: (event: React.MouseEvent | null) => void;
	valueOwner: EntityWithValueState;
	value?: LiteralValueState;
	valueType?: DynamicValueTypes;
	draggableObject?: DraggableObject;
	project: EditorService;
	onlyDefault?: boolean;
}

export type BinaryFormatMode =
	| 'base64'
	| 'hex'
	| 'json_array'
	| 'utf8'
	| 'file_upload';

// Convert Uint8Array to string representation in given mode
function uint8ArrayToModeString(
	bytes: Uint8Array,
	mode: BinaryFormatMode
): string {
	if (bytes.length === 0) return '';
	switch (mode) {
		case 'base64': {
			let binary = '';
			const len = bytes.byteLength;
			for (let i = 0; i < len; i++) {
				binary += String.fromCharCode(bytes[i]);
			}
			return btoa(binary);
		}
		case 'hex': {
			return Array.from(bytes)
				.map((b) => b.toString(16).padStart(2, '0'))
				.join(' ');
		}
		case 'json_array': {
			return JSON.stringify(Array.from(bytes));
		}
		case 'utf8': {
			return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
		}
		default:
			return '';
	}
}

// Parse string input into Uint8Array according to format mode
function parseInputToUint8Array(
	input: string,
	mode: BinaryFormatMode
): Uint8Array {
	const trimmed = input.trim();
	if (!trimmed) return new Uint8Array(0);

	switch (mode) {
		case 'base64': {
			// Remove whitespace/newlines if any
			const cleanBase64 = trimmed.replace(/\s+/g, '');
			const binaryString = atob(cleanBase64);
			const len = binaryString.length;
			const bytes = new Uint8Array(len);
			for (let i = 0; i < len; i++) {
				bytes[i] = binaryString.charCodeAt(i);
			}
			return bytes;
		}
		case 'hex': {
			// Remove 0x prefix, spaces, commas, colons
			const cleanHex = trimmed
				.replace(/0x/gi, '')
				.replace(/[\s,:]+/g, '');
			if (cleanHex.length % 2 !== 0) {
				throw new Error(
					'Hex string must contain an even number of characters.'
				);
			}
			if (!/^[0-9a-fA-F]*$/.test(cleanHex)) {
				throw new Error('Invalid character in hex string.');
			}
			const len = cleanHex.length / 2;
			const bytes = new Uint8Array(len);
			for (let i = 0; i < len; i++) {
				bytes[i] = parseInt(cleanHex.substring(i * 2, i * 2 + 2), 16);
			}
			return bytes;
		}
		case 'json_array': {
			const parsed = JSON.parse(trimmed);
			if (!Array.isArray(parsed)) {
				throw new Error('Input is not a valid JSON array.');
			}
			for (let i = 0; i < parsed.length; i++) {
				const num = Number(parsed[i]);
				if (!Number.isInteger(num) || num < 0 || num > 255) {
					throw new Error(
						`Item at index ${i} (${parsed[i]}) is not a valid byte (0-255).`
					);
				}
			}
			return new Uint8Array(parsed.map(Number));
		}
		case 'utf8': {
			return new TextEncoder().encode(input);
		}
		default:
			return new Uint8Array(0);
	}
}

// Auto-detect format mode from input text
function detectFormat(input: string): BinaryFormatMode {
	const trimmed = input.trim();
	if (!trimmed) return 'utf8';

	// 1. JSON Array check
	if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
		try {
			const parsed = JSON.parse(trimmed);
			if (
				Array.isArray(parsed) &&
				parsed.every((x) => typeof x === 'number' && x >= 0 && x <= 255)
			) {
				return 'json_array';
			}
		} catch (_) {}
	}

	// 2. Hex check (starts with 0x or contains space-separated hex pairs)
	const cleanHex = trimmed.replace(/0x/gi, '').replace(/[\s,:]+/g, '');
	if (/^[0-9a-fA-F]+$/.test(cleanHex) && cleanHex.length % 2 === 0) {
		// If input has spaces between hex bytes like "48 65 6c" or starts with 0x, strongly prefer hex
		if (
			trimmed.startsWith('0x') ||
			/\s/.test(trimmed) ||
			/^[0-9a-fA-F]{8,}$/.test(cleanHex)
		) {
			return 'hex';
		}
	}

	// 3. Base64 check
	const cleanBase64 = trimmed.replace(/\s+/g, '');
	if (
		cleanBase64.length % 4 === 0 &&
		/^[A-Za-z0-9+/]+={0,2}$/.test(cleanBase64) &&
		cleanBase64.length >= 8
	) {
		try {
			atob(cleanBase64);
			return 'base64';
		} catch (_) {}
	}

	// Default to utf8
	return 'utf8';
}

// Magic Header Detection
function detectMagicHeader(
	bytes: Uint8Array
): { label: string; mimeType: string } | null {
	if (bytes.length < 2) return null;

	// Helper for header slice matching
	const matchHeader = (sig: number[], offset = 0) => {
		if (bytes.length < offset + sig.length) return false;
		return sig.every((b, i) => bytes[offset + i] === b);
	};

	// PDF: %PDF-
	if (matchHeader([0x25, 0x50, 0x44, 0x46, 0x2d])) {
		return { label: 'PDF Document', mimeType: 'application/pdf' };
	}
	// PNG: \x89PNG\r\n\x1a\n
	if (matchHeader([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
		return { label: 'PNG Image', mimeType: 'image/png' };
	}
	// JPEG: FF D8 FF
	if (matchHeader([0xff, 0xd8, 0xff])) {
		return { label: 'JPEG Image', mimeType: 'image/jpeg' };
	}
	// GIF: GIF87a / GIF89a
	if (matchHeader([0x47, 0x49, 0x46, 0x38])) {
		return { label: 'GIF Image', mimeType: 'image/gif' };
	}
	// ZIP: PK \x03 \x04
	if (matchHeader([0x50, 0x4b, 0x03, 0x04])) {
		return {
			label: 'ZIP Archive / Office Document',
			mimeType: 'application/zip'
		};
	}
	// GZIP: \x1f\x8b
	if (matchHeader([0x1f, 0x8b])) {
		return { label: 'GZIP Compressed File', mimeType: 'application/gzip' };
	}
	// WebP: RIFF ... WEBP
	if (
		matchHeader([0x52, 0x49, 0x46, 0x46]) &&
		matchHeader([0x57, 0x45, 0x42, 0x50], 8)
	) {
		return { label: 'WebP Image', mimeType: 'image/webp' };
	}
	// OGG: OggS
	if (matchHeader([0x4f, 0x67, 0x67, 0x53])) {
		return { label: 'Ogg Media File', mimeType: 'application/ogg' };
	}
	// JSON check
	if (bytes[0] === 0x7b || bytes[0] === 0x5b) {
		return { label: 'JSON Document', mimeType: 'application/json' };
	}

	return null;
}

function formatByteSize(bytesCount: number): string {
	if (bytesCount < 1024) return `${bytesCount} bytes`;
	if (bytesCount < 1024 * 1024)
		return `${bytesCount} bytes (${(bytesCount / 1024).toFixed(1)} KB)`;
	return `${bytesCount} bytes (${(bytesCount / (1024 * 1024)).toFixed(2)} MB)`;
}

const BytesEditorDialog: React.FC<IBytesEditorDialogProps> = (props) => {
	const onlyDefault = props.onlyDefault || false;
	const valueOwnerCanvasParentEntity = getParentCanvasEntity(
		props.valueOwner
	);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// Parse initial value from rawString or value
	const initialRaw =
		props.value?.rawString ??
		(props.value?.value ? String(props.value.value) : '');

	const initialMode = useMemo(() => detectFormat(initialRaw), []);
	const [mode, setMode] = useState<BinaryFormatMode>(initialMode);
	const [inputText, setInputText] = useState<string>(initialRaw);
	const [parsedBytes, setParsedBytes] = useState<Uint8Array>(
		new Uint8Array(0)
	);
	const [parseError, setParseError] = useState<string | null>(null);
	const [autoDetectedMode, setAutoDetectedMode] =
		useState<BinaryFormatMode | null>(null);
	const [uploadedFileName, setUploadedFileName] = useState<string | null>(
		null
	);

	// Re-parse whenever inputText or mode changes
	useEffect(() => {
		if (mode === 'file_upload') return;
		try {
			const bytes = parseInputToUint8Array(inputText, mode);
			setParsedBytes(bytes);
			setParseError(null);
		} catch (err: any) {
			setParseError(err.message || 'Invalid binary data format.');
			setParsedBytes(new Uint8Array(0));
		}
	}, [inputText, mode]);

	// Auto-detection on pasting/text input change
	const handleTextChange = (text: string) => {
		setInputText(text);
		const detected = detectFormat(text);
		if (detected !== mode && text.trim().length > 4) {
			setAutoDetectedMode(detected);
		} else {
			setAutoDetectedMode(null);
		}
	};

	// Paste from Clipboard handler
	const handlePasteFromClipboard = async () => {
		try {
			const clipText = await navigator.clipboard.readText();
			if (!clipText) return;

			const detected = detectFormat(clipText);
			setMode(detected);
			setInputText(clipText);
			setAutoDetectedMode(detected);
		} catch (err: any) {
			setParseError(
				'Failed to read from clipboard. Please grant clipboard permissions.'
			);
		}
	};

	// Local file upload handler
	const handleFileSelect = (file: File) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			if (e.target?.result instanceof ArrayBuffer) {
				const bytes = new Uint8Array(e.target.result);
				setParsedBytes(bytes);
				setUploadedFileName(file.name);
				setParseError(null);
				// Convert to base64 for display in base64 mode
				const base64Str = uint8ArrayToModeString(bytes, 'base64');
				setInputText(base64Str);
				setMode('base64');
				setAutoDetectedMode('base64');
			}
		};
		reader.onerror = () => {
			setParseError('Error reading selected file.');
		};
		reader.readAsArrayBuffer(file);
	};

	// Magic header detection for preview
	const magicHeader = useMemo(
		() => detectMagicHeader(parsedBytes),
		[parsedBytes]
	);

	// Preview strings
	const hexDumpPreview = useMemo(() => {
		if (parsedBytes.length === 0) return '';
		const slice = parsedBytes.slice(0, 64);
		return (
			Array.from(slice)
				.map((b) => b.toString(16).padStart(2, '0'))
				.join(' ') + (parsedBytes.length > 64 ? ' ...' : '')
		);
	}, [parsedBytes]);

	const textPreview = useMemo(() => {
		if (parsedBytes.length === 0) return '';
		const slice = parsedBytes.slice(0, 128);
		return (
			new TextDecoder('utf-8', { fatal: false }).decode(slice) +
			(parsedBytes.length > 128 ? '...' : '')
		);
	}, [parsedBytes]);

	const handleSave = (e: any) => {
		if (parseError) return;

		let storage: ChangeSet | IValueStoreClient =
			props.project.localTestValues;

		console.log('onlyDefault');

		if (onlyDefault) {
			storage = props.project.logic.addChangeSet(
				new ChangeSet(
					props.project.logic,
					ProjectState.sessionAuthor,
					new Date().toISOString(),
					props.valueOwner,
					false,
					StateMutationAction.SetPrimitiveDefaultValue
				)
			);
		}

		// Store base64 representation or raw string as value
		const finalString = uint8ArrayToModeString(parsedBytes, 'base64');

		const changeSet = handleLiteralValueChange(
			finalString,
			props.valueOwner,
			props.project.logic,
			storage,
			{
				rawString: finalString
			}
		);

		if (!!changeSet) {
			props.project.renderAndCloseChangeSet(changeSet);
		}

		props.project.emit(valueOwnerCanvasParentEntity.id, {});
		props.project.test?.validate();
		props.onClose?.(e);
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '12px',
				padding: '32px',
				maxHeight: '90vh',
				overflowY: 'auto'
			}}
		>
			{/* Header */}
			<span>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '16px'
					}}
				>
					<button
						className="dialog_back_button"
						onClick={(e) => props.onClose?.(e)}
					>
						<i className="fa-solid fa-arrow-left"></i>
					</button>
					<h1
						style={{ margin: 0, fontWeight: 500, fontSize: '26px' }}
					>
						Edit Binary / Bytes Value
					</h1>
				</div>
				<p
					style={{
						margin: '4px 0 0 42px',
						color: 'rgba(0,0,0,0.6)',
						fontSize: '14px'
					}}
				>
					Enter, paste, or upload binary data in Base64, Hexadecimal,
					JSON Byte Array, or UTF-8 text.
				</p>
			</span>

			{/* Mode Selector & Clipboard Toolbar */}
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				gap={2}
				sx={{ mt: 1 }}
			>
				<FormControl size="small" sx={{ minWidth: 260 }}>
					<InputLabel id="binary-format-mode-label">
						Add binary value as...
					</InputLabel>
					<Select
						labelId="binary-format-mode-label"
						id="binary-format-mode-select"
						value={mode}
						label="Add binary value as..."
						onChange={(e) => {
							const newMode = e.target.value as BinaryFormatMode;
							setMode(newMode);
							setAutoDetectedMode(null);
							if (
								parsedBytes.length > 0 &&
								newMode !== 'file_upload'
							) {
								setInputText(
									uint8ArrayToModeString(parsedBytes, newMode)
								);
							}
						}}
					>
						<MenuItem value="base64">
							Base64 String (e.g. SGVsbG8=)
						</MenuItem>
						<MenuItem value="hex">
							Hexadecimal (e.g. 48 65 6c 6c 6f)
						</MenuItem>
						<MenuItem value="json_array">
							Byte Array (e.g. [72, 101, 108])
						</MenuItem>
						<MenuItem value="utf8">UTF-8 Plain Text</MenuItem>
						<MenuItem value="file_upload">
							Upload Local File...
						</MenuItem>
					</Select>
				</FormControl>

				<Stack direction="row" gap={1}>
					<Button
						variant="outlined"
						size="small"
						startIcon={<ContentPasteIcon />}
						onClick={handlePasteFromClipboard}
					>
						Paste Clipboard
					</Button>
					<Button
						variant="outlined"
						size="small"
						startIcon={<UploadFileIcon />}
						onClick={() => fileInputRef.current?.click()}
					>
						Upload File
					</Button>
					<input
						type="file"
						ref={fileInputRef}
						style={{ display: 'none' }}
						onChange={(e) => {
							if (e.target.files && e.target.files[0]) {
								handleFileSelect(e.target.files[0]);
							}
						}}
					/>
				</Stack>
			</Stack>

			{/* Auto-detection Alert Banner */}
			{autoDetectedMode && (
				<Alert
					severity="info"
					icon={<AutoAwesomeIcon fontSize="inherit" />}
					action={
						<Button
							color="inherit"
							size="small"
							onClick={() => {
								setMode(autoDetectedMode);
								setAutoDetectedMode(null);
							}}
						>
							Switch to {autoDetectedMode.toUpperCase()}
						</Button>
					}
					sx={{ py: 0.5 }}
				>
					Detected input pattern matches{' '}
					<b>{autoDetectedMode.toUpperCase()}</b> format.
				</Alert>
			)}

			{/* File Upload Dropzone or Text Area */}
			{mode === 'file_upload' ? (
				<Box
					sx={{
						border: '2px dashed #ccc',
						borderRadius: '8px',
						p: 4,
						textAlign: 'center',
						backgroundColor: 'rgba(0,0,0,0.02)',
						cursor: 'pointer',
						'&:hover': {
							backgroundColor: 'rgba(0,0,0,0.04)',
							borderColor: '#1976d2'
						}
					}}
					onClick={() => fileInputRef.current?.click()}
					onDragOver={(e) => e.preventDefault()}
					onDrop={(e) => {
						e.preventDefault();
						if (e.dataTransfer.files && e.dataTransfer.files[0]) {
							handleFileSelect(e.dataTransfer.files[0]);
						}
					}}
				>
					<UploadFileIcon
						sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
					/>
					<p style={{ margin: 0, fontWeight: 500 }}>
						Click or Drag & Drop a file here to convert it to binary
						bytes
					</p>
					{uploadedFileName && (
						<Chip
							label={`Loaded file: ${uploadedFileName}`}
							color="primary"
							sx={{ mt: 1.5 }}
						/>
					)}
				</Box>
			) : (
				<textarea
					value={inputText}
					onChange={(e) => handleTextChange(e.target.value)}
					placeholder={`Paste or type ${mode.toUpperCase()} binary representation here...`}
					style={{
						width: '100%',
						height: '180px',
						fontFamily: 'monospace',
						fontSize: '13px',
						padding: '12px',
						borderRadius: '6px',
						border: parseError
							? '1px solid #d32f2f'
							: '1px solid #ccc',
						outline: 'none',
						resize: 'vertical',
						backgroundColor: '#fafafa'
					}}
				/>
			)}

			{/* Error Message */}
			{parseError && (
				<Alert severity="error" icon={<ErrorOutlineIcon />}>
					{parseError}
				</Alert>
			)}

			{/* Status & Preview Badges */}
			{!parseError && parsedBytes.length > 0 && (
				<Box
					sx={{
						p: 2,
						borderRadius: '6px',
						backgroundColor: '#f5f5f5',
						border: '1px solid #e0e0e0'
					}}
				>
					<Stack
						direction="row"
						alignItems="center"
						gap={1.5}
						flexWrap="wrap"
						sx={{ mb: 1.5 }}
					>
						<Chip
							icon={<CheckCircleOutlineIcon />}
							label={`Valid Binary (${formatByteSize(parsedBytes.length)})`}
							color="success"
							size="small"
						/>
						{magicHeader && (
							<Chip
								icon={<DataObjectIcon />}
								label={`Detected Type: ${magicHeader.label} (${magicHeader.mimeType})`}
								color="primary"
								variant="outlined"
								size="small"
							/>
						)}
					</Stack>

					{/* Previews */}
					<Stack
						gap={1}
						sx={{ fontSize: '12px', fontFamily: 'monospace' }}
					>
						<div>
							<b style={{ color: 'rgba(0,0,0,0.6)' }}>
								Hex Dump:
							</b>{' '}
							<span
								style={{
									color: '#0d47a1',
									wordBreak: 'break-all'
								}}
							>
								{hexDumpPreview}
							</span>
						</div>
						<div>
							<b style={{ color: 'rgba(0,0,0,0.6)' }}>
								Text Snippet:
							</b>{' '}
							<span
								style={{
									color: '#2e7d32',
									wordBreak: 'break-all'
								}}
							>
								{textPreview}
							</span>
						</div>
					</Stack>
				</Box>
			)}

			{/* Dialog Actions */}
			<section
				style={{
					display: 'flex',
					justifyContent: 'flex-end',
					gap: '16px',
					paddingTop: '16px'
				}}
			>
				<Button variant="text" onClick={(e: any) => props.onClose?.(e)}>
					CANCEL
				</Button>

				<Button
					onClick={handleSave}
					variant="contained"
					color="primary"
					disabled={!!parseError}
				>
					SAVE VALUE
				</Button>
			</section>
		</div>
	);
};

export default BytesEditorDialog;
